/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import React from "react";
import { cn } from "@/lib/utils/classNames";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  placeholder?: string;
  containerClassName?: string;
  inputClassName?: string;
  type?: "text" | "email" | "password" | "textarea";
  error?: string;
  labelClassName?: string;
  allowContainerResize?: boolean; // allow container resize with textarea
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  errorClassName?: string;
}

const Input = ({
  label,
  value,
  onChange,
  placeholder,
  containerClassName = "",
  inputClassName = "",
  type = "text",
  error,
  labelClassName = "",
  allowContainerResize = false,
  leftIcon,
  rightIcon,
  errorClassName = "",
  ...props
}: InputProps) => {
  const getSafeTextareaProps = (props: any) => {
    const allowed = [
      "placeholder",
      "rows",
      "className",
      "style",
      "disabled",
      "required",
      "name",
      "id",
      "maxLength",
      "minLength",
      "autoFocus",
      "readOnly",
      "spellCheck",
      "wrap",
      "tabIndex",
    ];
    const ariaProps = Object.keys(props).filter((k) => k.startsWith("aria-"));
    const safe: any = {};
    for (const key of allowed)
      if (props[key] !== undefined) safe[key] = props[key];
    for (const key of ariaProps) safe[key] = props[key];
    return safe;
  };
  const restTextareaProps = getSafeTextareaProps(props);

  return (
    <div
      className={cn(
        `ui-glass-input-wrap relative flex justify-center flex-col w-full rounded-none
         bg-gradient-to-br from-[#7f1d1d]/15 via-black/35 to-black/55
         border border-white/15 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]
         px-4 py-3 gap-1 
         focus-within:ring-0 focus-within:outline-none focus-within:border-red-500/35`,
        containerClassName
      )}
    >
      {label && (
        <span
          className={cn(
            `text-xs font-normal text-white bg-transparent`,
            labelClassName
          )}
        >
          {label}
        </span>
      )}
      {type === "textarea" ? (
        <div
          className={`flex-1 ${
            allowContainerResize ? "resize-y min-h-[40px] max-h-[400px]" : ""
          }`}
          style={allowContainerResize ? { minHeight: 44, maxHeight: 400 } : {}}
        >
          <textarea
            className={cn(
              "text-base bg-transparent outline-none border-none text-main-dark placeholder:text-sub1-gray font-normal w-full",
              allowContainerResize
                ? "resize-y h-full min-h-full"
                : "resize-none",
              inputClassName || ""
            )}
            value={value as string}
            onChange={(e) => {
              if (onChange) onChange(e as any);
            }}
            placeholder={placeholder}
            {...(allowContainerResize ? {} : { rows: 4 })}
            style={
              {
                ...(allowContainerResize
                  ? { minHeight: "100%", maxHeight: "100%" }
                  : {}),
              } as React.CSSProperties
            }
            {...restTextareaProps}
          />
        </div>
      ) : (
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 text-main-gray z-10">
              {leftIcon}
            </div>
          )}
          <input
            className={cn(
              `text-sm bg-transparent 
              outline-none border-none text-main-dark placeholder:text-sub1-gray 
              font-normal w-full placeholder:text-sm
              focus:ring-0 focus:outline-none`,
              leftIcon ? "pl-8" : "",
              rightIcon ? "pr-8" : "",
              inputClassName
            )}
            value={value as string}
            onChange={onChange}
            type={type}
            placeholder={placeholder}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 text-main-gray z-10">
              {rightIcon}
            </div>
          )}
        </div>
      )}
      {error && (
        <span
          className={cn("py-1 translate-y-full text-[#a86a6b] text-[10px] lg:text-xs text-left w-full absolute bottom-0", errorClassName)}
          style={{
            textShadow: "0 1px 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)",
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default Input;
